import {
  C3D_CELL,
  C3D_COLS,
  C3D_IDLE_FRAMES,
  C3D_IDLE_MS,
  type CharacterAppearance,
} from "@ghostland/shared";
import { compositeAtlas } from "./c3dPipeline.js";

/** Centre of the sprite's opaque pixels, in cell pixels, for centring in the canvas. */
interface Bounds {
  cx: number;
  cy: number;
}

/**
 * Scan the front-row idle frames (cols 0…IDLE-1, row 0) and return the centre of their
 * unioned opaque bounding box. The union across frames keeps the idle sway from jittering
 * the centring — we centre the character's overall stance, not each individual frame.
 */
function idleBounds(atlas: HTMLCanvasElement): Bounds {
  const fallback = { cx: C3D_CELL / 2, cy: C3D_CELL / 2 };
  const w = C3D_IDLE_FRAMES * C3D_CELL;
  const actx = atlas.getContext("2d", { willReadFrequently: true });
  if (!actx) return fallback;
  let data: Uint8ClampedArray;
  try {
    data = actx.getImageData(0, 0, w, C3D_CELL).data;
  } catch {
    return fallback; // tainted canvas — fall back to the cell centre
  }
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let f = 0; f < C3D_IDLE_FRAMES; f++) {
    const ox = f * C3D_CELL;
    for (let y = 0; y < C3D_CELL; y++) {
      for (let lx = 0; lx < C3D_CELL; lx++) {
        if (data[(y * w + ox + lx) * 4 + 3] > 16) {
          if (lx < minX) minX = lx;
          if (lx > maxX) maxX = lx;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
  }
  if (minX > maxX) return fallback;
  return { cx: (minX + maxX + 1) / 2, cy: (minY + maxY + 1) / 2 };
}

/**
 * DOM-canvas idle animator for the creator/selection screens: draws the front-facing
 * idle loop at 6fps, pixel-crisp. Composites the FULL appearance (body + clothing
 * layers + hair + hat) via c3dComposite, so equipped gear shows. No Phaser involved.
 */
export interface IdlePreview {
  setAppearance(a: CharacterAppearance): void;
  /** Deprecated debug hook (hair offsets are baked from the catalog now); no-op. */
  setHairOffset(px: number | null): void;
  dispose(): void;
}

export function attachIdlePreview(canvas: HTMLCanvasElement, appearance: CharacterAppearance): IdlePreview {
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  let atlas: HTMLCanvasElement | null = null;
  let bounds: Bounds = { cx: C3D_CELL / 2, cy: C3D_CELL / 2 };
  let token = 0;
  let frame = 0;
  let last = 0;
  let raf = 0;
  let disposed = false;

  async function build(a: CharacterAppearance): Promise<void> {
    const my = ++token;
    const c = await compositeAtlas(a);
    if (disposed || my !== token || !c) return;
    atlas = c;
    bounds = idleBounds(c);
  }

  function draw(t: number): void {
    if (disposed) return;
    raf = requestAnimationFrame(draw);
    if (t - last >= C3D_IDLE_MS) {
      last = t;
      frame = (frame + 1) % C3D_IDLE_FRAMES;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (atlas) {
      const sx = (frame % C3D_COLS) * C3D_CELL;
      // Draw the full cell at the canvas scale, but translate so the sprite's opaque
      // centre lands at the canvas centre (the art isn't centred within its cell).
      const s = canvas.width / C3D_CELL;
      const dx = canvas.width / 2 - bounds.cx * s;
      const dy = canvas.height / 2 - bounds.cy * s;
      ctx.drawImage(atlas, sx, 0, C3D_CELL, C3D_CELL, dx, dy, C3D_CELL * s, C3D_CELL * s);
    }
  }

  void build({ ...appearance });
  raf = requestAnimationFrame(draw);

  return {
    setAppearance(a: CharacterAppearance): void {
      void build({ ...a });
    },
    setHairOffset(): void {
      /* deprecated no-op */
    },
    dispose(): void {
      disposed = true;
      cancelAnimationFrame(raf);
    },
  };
}
